import { Skeleton } from "antd";
import { useContext, useId } from "react";
import mascotUrl from "../../assets/image/dashboard-mascot.webp";
import { HomeContext } from "../../containers/Home";
import {
	useMapProjects,
	useMyOpenReportsCount,
	useMyProjectsCount,
	useMyTasksSummary,
} from "../../hooks/useDashboardQueries";
import { formatCount } from "./dashboardConfig";
import { useGuardedNavigate } from "./widgetHooks";

interface WelcomeCardProps {
	currentTenantKey: string;
	/** The user's tenants; My projects is summed over them. */
	tenantKeys: string[];
	/** Reporting, home of the My reports table, is open to the user in the current tenant. */
	showMyReports: boolean;
	/** Well-examiner session: no My tasks, and My projects counts the current tenant's map. */
	restricted: boolean;
}

interface StatProps {
	label: string;
	value: number | undefined;
	isLoading: boolean;
	/** Set when the value only covers part of the data: shown as "N+", with this as its hint. */
	partialNote?: string;
	/** Makes the stat a link, behind the unsaved-changes guard. */
	to?: string;
}

const renderStatValue = ({
	value,
	isLoading,
	partialNote,
}: Pick<StatProps, "value" | "isLoading" | "partialNote">) => {
	if (isLoading) return <Skeleton.Button active size="small" />;
	if (value === undefined) {
		return <span title="This number could not be loaded">–</span>;
	}
	return partialNote ? (
		<span title={partialNote}>{formatCount(value, true)}</span>
	) : (
		formatCount(value)
	);
};

function Stat({ label, to, ...value }: StatProps) {
	const { onLinkClick } = useGuardedNavigate();
	const content = (
		<>
			<div className="dashboard-welcome__stat-label">{label}</div>
			<div className="dashboard-welcome__stat-value">
				{renderStatValue(value)}
			</div>
		</>
	);

	return to ? (
		<a
			href={to}
			className="dashboard-welcome__stat dashboard-welcome__stat-link"
			onClick={onLinkClick(to)}
		>
			{content}
		</a>
	) : (
		<div className="dashboard-welcome__stat">{content}</div>
	);
}

/** Greeting and the user's own counts across Terra. */
function WelcomeCard({
	currentTenantKey,
	tenantKeys,
	showMyReports,
	restricted,
}: WelcomeCardProps) {
	const titleId = useId();
	const { userInfo }: any = useContext(HomeContext);
	const email: string | undefined = userInfo?.user?.email;
	const name = userInfo?.user?.name || email?.split("@")[0] || "";

	const tasks = useMyTasksSummary(
		restricted ? null : { tenantKey: currentTenantKey, scope: "all" },
		email
	);
	const myProjects = useMyProjectsCount(
		restricted ? null : currentTenantKey,
		tenantKeys,
		email
	);
	const openReports = useMyOpenReportsCount(
		showMyReports ? currentTenantKey : null,
		email
	);
	// Restricted sessions may only read the map, which counts their projects the same way.
	const mapProjects = useMapProjects(restricted ? currentTenantKey : null);
	const projects = restricted
		? { value: mapProjects.data?.length, isLoading: mapProjects.isLoading }
		: {
				value: tenantKeys.length ? myProjects.data?.count : 0,
				isLoading: myProjects.isLoading,
				partialNote: myProjects.data?.truncated
					? "Counted over the first 50 of your tenants"
					: undefined,
			};

	return (
		<section
			className="dashboard-welcome dashboard-widget--welcome dashboard-widget--short"
			aria-labelledby={titleId}
		>
			<div className="dashboard-welcome__top">
				<img className="dashboard-welcome__mascot" src={mascotUrl} alt="" />
				<div className="dashboard-welcome__intro">
					<h1 id={titleId} className="dashboard-welcome__title">
						Welcome, {name}!
						<br />
						Everything you need is here in Terra.
					</h1>
					<p className="dashboard-welcome__subtitle">
						Your centralised hub to run your operations with ease.
					</p>
				</div>
			</div>
			<div className="dashboard-welcome__stats">
				{!restricted && (
					<Stat
						label="My tasks"
						value={tasks.data?.totals?.pending}
						isLoading={tasks.isLoading}
						partialNote={
							tasks.data?.truncated
								? "The task source limit was reached; you have at least this many"
								: undefined
						}
						to="/my-tasks"
					/>
				)}
				<Stat label="My projects" {...projects} to="/projects" />
				{showMyReports && (
					<Stat
						label="My open reports"
						value={openReports.data}
						isLoading={openReports.isLoading}
						to="/reporting/my-reports"
					/>
				)}
			</div>
		</section>
	);
}

export default WelcomeCard;
